/**
 * Storiz design tokens — single source of truth for inline-style usage.
 *
 * Tailwind utilities are defined in `index.css` `@theme` and resolve to the
 * same values as the objects below. Use these constants only when a Tailwind
 * class won't work (dynamic colors in inline `style={{}}`, SVG `stroke=`,
 * gradients, runtime computed colors, etc.).
 *
 * Scope of this module: Pass 1 of the UI polish brief — tokens only. The
 * `BRAND` object is kept around as a backwards-compat alias for existing
 * customer-page imports; new code should prefer the named exports below
 * (`COLORS`, `TYPE`, `SPACING`, `RADIUS`, `SHADOW`, `MOTION`).
 */

/* ─── Colors ──────────────────────────────────────────────────────────────── */

export const COLORS = {
  // Surfaces — never pure white as the canvas.
  bgPrimary:  '#FAF7F2',
  bgCard:     '#FFFFFF',
  bgElevated: '#F4EFE7',

  // Text — warm brown instead of pure black.
  textPrimary:   '#2E2418',
  textSecondary: '#6B5D4F',
  textTertiary:  '#9C8F7E',

  // Brand accent — mustard.
  accentPrimary: '#C9A04C',
  accentHover:   '#B89240',
  accentSoft:    '#F0E4C9',

  // Semantic — warm-tinted, not bright SaaS colors.
  success: '#7A8B6F',  // warm sage
  warning: '#D49B5C',  // warm amber
  error:   '#B85C3D',  // terracotta
  info:    '#6B7F8C',  // muted blue-grey

  // Borders — subtle is the default.
  borderSubtle:  '#E8E0D2',
  borderDefault: '#D4C9B5',
  borderStrong:  '#9C8F7E',
} as const;

/**
 * ADMIN NIGHT MODE (2026-10-02) — the same palette as `COLORS`, as CSS
 * variables. Each one resolves to EXACTLY the COLORS value in light (index.css
 * @theme declares the same hexes) and to its warm-dark twin under
 * html.adm-dark. Admin inline styles use these; customer pages keep COLORS.
 * Pinned to COLORS by lib/theme/listsDark.guard.test.ts.
 */
export const ADM_COLORS = {
  bgPrimary:     'var(--color-bg-primary)',
  bgCard:        'var(--color-bg-card)',
  bgElevated:    'var(--color-bg-elevated)',
  textPrimary:   'var(--color-text-primary)',
  textSecondary: 'var(--color-text-secondary)',
  textTertiary:  'var(--color-text-tertiary)',
  accentPrimary: 'var(--color-accent-primary)',
  accentHover:   'var(--color-accent-hover)',
  accentSoft:    'var(--color-accent-soft)',
  success:       'var(--color-success)',
  warning:       'var(--color-warning)',
  error:         'var(--color-error)',
  info:          'var(--color-info)',
  borderSubtle:  'var(--color-border-subtle)',
  borderDefault: 'var(--color-border-default)',
  borderStrong:  'var(--color-border-strong)',
} as const satisfies Record<keyof typeof COLORS, string>;

/**
 * A colour that may be a var(), at an alpha: two hex digits (`'44'`, what
 * `${COLORS.error}44` meant) or a fraction (`0.1`, what rgba(…, .1) meant).
 * color-mix with transparent is exact, so the light page paints the same.
 */
export function withAlpha(color: string, alpha: string | number): string {
  const a = typeof alpha === 'number' ? alpha : parseInt(alpha, 16) / 255;
  const pct = Math.round(a * 1000) / 10;
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
}

/* ─── Typography ──────────────────────────────────────────────────────────── */

// Font stacks. Pass 2 will install Fraunces + Inter and apply these.
// Today they alias the existing fonts so this pass is visually inert.
export const FONT_STACK = {
  heading: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
  body:    "'Inter', 'DM Sans', system-ui, -apple-system, sans-serif",
  // Reserved for the bold display fonts on album covers themselves —
  // explicitly NOT to be applied to UI per the brief.
  coverDisplay: "'Londrina Solid', sans-serif",
} as const;

// Type scale — { size, lineHeight, weight, optional letterSpacing }.
// Pass 2 wires these into reusable utility classes.
export const TYPE = {
  display:    { size: '48px', lineHeight: 1.1, weight: 600 },
  h1:         { size: '32px', lineHeight: 1.2, weight: 600 },
  h2:         { size: '24px', lineHeight: 1.3, weight: 600 },
  h3:         { size: '20px', lineHeight: 1.4, weight: 500 },
  bodyText:   { size: '15px', lineHeight: 1.6, weight: 400 },
  small:      { size: '13px', lineHeight: 1.5, weight: 400 },
  caption:    { size: '12px', lineHeight: 1.4, weight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' as const },
} as const;

/* ─── Spacing (4px base) ──────────────────────────────────────────────────── */

export const SPACING = {
  '1':  '4px',
  '2':  '8px',
  '3':  '12px',
  '4':  '16px',
  '5':  '20px',
  '6':  '24px',
  '8':  '32px',
  '10': '40px',
  '12': '48px',
  '16': '64px',
  '20': '80px',
  '24': '96px',
} as const;

/* ─── Radius ──────────────────────────────────────────────────────────────── */

export const RADIUS = {
  sm: '6px',   // badges, small chips
  md: '10px',  // buttons, inputs
  lg: '14px',  // cards
  xl: '20px',  // large containers
} as const;

/* ─── Shadows — very subtle, warm-tinted. Prefer borders. ─────────────────── */

export const SHADOW = {
  sm: '0 1px 2px rgba(46, 36, 24, 0.04)',
  md: '0 2px 8px rgba(46, 36, 24, 0.06)',
  lg: '0 8px 24px rgba(46, 36, 24, 0.08)',
} as const;

/* ─── Motion ──────────────────────────────────────────────────────────────── */

export const MOTION = {
  hover: '150ms ease',
  state: '200ms ease',
} as const;

/* ─────────────────────────────────────────────────────────────────────────────
   Backwards-compat aliases — imported by existing customer pages
   (AlbumFlowPage, MagicUploadPage, AlbumMobilePage, AlbumEditorPage). Mapped
   into the new color system so future migrations don't silently change
   visual intent. New code should import COLORS / TYPE / etc. directly.
   ───────────────────────────────────────────────────────────────────────────── */

export const BRAND = {
  // ⚠️ Pinned to the existing values used by customer pages today. Pass 1 is
  // visually inert — the new warm-palette tokens are exposed via COLORS /
  // CSS @theme, but components using `BRAND.gold` etc. keep rendering the
  // exact same shades they did before. Migration to COLORS happens
  // component-by-component in later passes.
  gold:      '#C9A24A',
  goldLight: '#D4B86A',
  goldDeep:  '#A8842F',
  goldDark:  '#8B6F26',

  white:      '#FFFFFF',
  dark:       '#1F2937',
  muted:      '#6B7280',
  beige:      '#F5EFE0',
  beigeLight: '#FAF6EC',
  border:     '#E5E0D2',

  success: '#10B981',
  error:   '#EF4444',
  warning: '#F59E0B',
} as const;

export const FONTS = {
  display: FONT_STACK.heading,
  brand:   FONT_STACK.coverDisplay,
  body:    FONT_STACK.body,
} as const;
